import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import dayjs from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';

import {WRITABLE_ENTRY_KINDS, WRITABLE_ENTRY_KIND_VALUES} from './calendarSources';
import {apiErrorMessage} from './errorText';

/** Vorbelegung aus dem Raster: gezogener Zeitraum, oder leer für „von Hand anlegen". */
export interface EntryDraft {
    /** Tag im Format `YYYY-MM-DD`. */
    date: string;
    /** Beginn als `HH:mm`. */
    from: string;
    /** Ende als `HH:mm`. */
    to: string;
}

interface Props {
    open: boolean;
    draft: EntryDraft | null;
    onClose: () => void;
    /** Nach erfolgreichem Anlegen — die Seite lädt den Zeitraum neu. */
    onCreated: () => void;
}

/** Übersetzungsschlüssel je Eintragsart. */
const kindKey = (kind: number): string => {
    switch (kind) {
        case WRITABLE_ENTRY_KINDS.vacation:
            return 'calendar.kinds.vacation';
        case WRITABLE_ENTRY_KINDS.sick:
            return 'calendar.kinds.sick';
        case WRITABLE_ENTRY_KINDS.training:
            return 'calendar.kinds.training';
        case WRITABLE_ENTRY_KINDS.standby:
            return 'calendar.kinds.standby';
        default:
            return 'calendar.kinds.appointment';
    }
};

/**
 * Einen Eintrag im eigenen Kalender anlegen.
 *
 * Die Personalnummer ist bewusst kein Feld: der Eintrag entsteht immer für den angemeldeten
 * Benutzer (der Server leitet die Person aus dem LabelBenutzer ab). Neben dem freien Termin lassen
 * sich Urlaub, Krank, Schulung und Bereitschaft eintragen — jeweils als **ein Tag**, weil ein
 * mehrtägiger Balken in Labelwin aus einem Kopfsatz und einem Tagessatz je Werktag besteht und
 * dessen Arbeitszeitlogik hier nicht nachgebildet wird.
 */
export const EntryEditorDialog: React.FC<Props> = ({open, draft, onClose, onCreated}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();

    const [kind, setKind] = useState<number>(WRITABLE_ENTRY_KINDS.appointment);
    const [date, setDate] = useState('');
    const [from, setFrom] = useState('09:00');
    const [to, setTo] = useState('10:00');
    const [subject, setSubject] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setKind(WRITABLE_ENTRY_KINDS.appointment);
        setDate(draft?.date ?? dayjs().format('YYYY-MM-DD'));
        setFrom(draft?.from ?? '09:00');
        setTo(draft?.to ?? '10:00');
        setSubject('');
    }, [open, draft]);

    /*
     * Eine Abwesenheit füllt standardmäßig den Arbeitstag und trägt ihre Bezeichnung als Betreff —
     * niemand schreibt gern „Urlaub" in ein Feld, wenn er gerade „Urlaub" ausgewählt hat. Beides
     * bleibt änderbar.
     */
    const chooseKind = (next: number): void => {
        setKind(next);
        if (next !== WRITABLE_ENTRY_KINDS.appointment) {
            setFrom('08:00');
            setTo('16:00');
            if (!subject.trim()) setSubject(t(kindKey(next)));
        }
    };

    const valid = date.length > 0 && from.length > 0 && to.length > 0
        && from < to && subject.trim().length > 0;

    const save = async (): Promise<void> => {
        setBusy(true);
        try {
            // Wanduhrzeit ohne Zeitzone: genau das, was die Plantafel führt. Ein `Z` würde den
            // Eintrag im Sommer zwei Stunden verschoben speichern — ohne Fehlermeldung.
            await context.api.calendar.calendarCreateEntry({
                start: `${date}T${from}:00`,
                end: `${date}T${to}:00`,
                subject: subject.trim(),
                kind,
            });
            enqueueSnackbar(t('calendar.created'), {variant: 'success'});
            onCreated();
            onClose();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('calendar.saveFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('calendar.newEntry')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <TextField select fullWidth size="small" label={t('calendar.entryKind')}
                               value={kind}
                               onChange={(e) => chooseKind(Number(e.target.value))}>
                        {WRITABLE_ENTRY_KIND_VALUES.map((value) => (
                            <MenuItem key={value} value={value}>{t(kindKey(value))}</MenuItem>
                        ))}
                    </TextField>

                    <TextField fullWidth size="small" type="date" label={t('calendar.field.date')}
                               value={date}
                               slotProps={{inputLabel: {shrink: true}}}
                               onChange={(e) => setDate(e.target.value)}/>

                    <Stack direction="row" spacing={2}>
                        <TextField fullWidth size="small" type="time" label={t('calendar.field.from')}
                                   value={from}
                                   slotProps={{inputLabel: {shrink: true}}}
                                   onChange={(e) => setFrom(e.target.value)}/>
                        <TextField fullWidth size="small" type="time" label={t('calendar.field.to')}
                                   value={to}
                                   slotProps={{inputLabel: {shrink: true}}}
                                   onChange={(e) => setTo(e.target.value)}/>
                    </Stack>

                    <TextField fullWidth size="small" label={t('calendar.field.subject')}
                               value={subject}
                               slotProps={{htmlInput: {maxLength: 50}}}
                               onChange={(e) => setSubject(e.target.value)}
                               helperText={t('calendar.subjectHint')}/>

                    {kind !== WRITABLE_ENTRY_KINDS.appointment && (
                        <Alert severity="info">{t('calendar.absenceSingleDayHint')}</Alert>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('calendar.cancel')}</Button>
                <Button variant="contained" onClick={() => void save()} disabled={busy || !valid}>
                    {t('calendar.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EntryEditorDialog;
